import React, { useEffect, useMemo, useState } from 'react';
import Icon from "components/ui/AppIcon.jsx";
import Button from '../../../components/ui/Button.jsx';
import Input from '../../../components/ui/Input';
import { getComplianceGridRows } from '../../../utils/api';

const statusOptions = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'compliant', label: 'Conforme' },
  { value: 'warning', label: 'Advertencia' },
  { value: 'non_compliant', label: 'No conforme' },
  { value: 'pending', label: 'Pendiente' }
];

const getStatusColor = (status) => {
  switch (status) {
    case 'compliant':     return 'text-success bg-success/10 border-success/20';
    case 'warning':       return 'text-warning bg-warning/10 border-warning/20';
    case 'non_compliant': return 'text-error bg-error/10 border-error/20';
    case 'pending':       return 'text-primary bg-primary/10 border-primary/20';
    default:              return 'text-muted-foreground bg-muted border-border';
  }
};
const getStatusLabel = (status) => {
  switch (status) {
    case 'compliant': return 'Conforme';
    case 'warning':   return 'Advertencia';
    case 'non_compliant': return 'No conforme';
    case 'pending':   return 'Pendiente';
    default:          return 'Desconocido';
  }
};
const getStatusIcon = (status) => {
  switch (status) {
    case 'compliant':     return 'CheckCircle';
    case 'warning':       return 'AlertTriangle';
    case 'non_compliant': return 'XCircle';
    case 'pending':       return 'Clock';
    default:              return 'Circle';
  }
};

function formatDDMMYYYY(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

function exportCsv(rows) {
  if (!rows?.length) return;
  const header = ['id','tag','instrument','type','certification','status','lastCalibration','nextDue','accuracy','technician','location','serial'];
  const lines = [header.join(',')].concat(rows.map(r => ([
    r.id, r.tag, `"${r.instrument}"`, r.type, r.certification, r.status,
    r.lastCalibration ? new Date(r.lastCalibration).toISOString() : '',
    r.nextDue ? new Date(r.nextDue).toISOString() : '',
    r.accuracy ?? '',
    `"${r.technician}"`, `"${r.location}"`, r.serial
  ].join(','))));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `compliance_grid_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ComplianceGrid = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('instrument'); // 'instrument' | 'type' | 'nextDue' | 'accuracy'
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getComplianceGridRows({ days: 365 });
        setRows(data || []);
      } catch (e) {
        console.error('[ComplianceGrid] load error', e);
        setError(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const filteredAndSorted = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();
    const filtered = (rows || []).filter(item => {
      const matchesSearch =
        (item.instrument || '').toLowerCase().includes(term) ||
        (item.tag || '').toLowerCase().includes(term) ||
        (item.technician || '').toLowerCase().includes(term) ||
        (item.serial || '').toLowerCase().includes(term);
      const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
      return matchesSearch && matchesStatus;
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      if (sortField === 'nextDue') {
        const ad = a.nextDue ? new Date(a.nextDue).getTime() : Number.POSITIVE_INFINITY;
        const bd = b.nextDue ? new Date(b.nextDue).getTime() : Number.POSITIVE_INFINITY;
        return (ad - bd) * dir;
      }
      if (sortField === 'accuracy') {
        const av = a.accuracy ?? -1;
        const bv = b.accuracy ?? -1;
        return (av - bv) * dir;
      }
      const av = (a[sortField] || '').toString().toLowerCase();
      const bv = (b[sortField] || '').toString().toLowerCase();
      return av.localeCompare(bv) * dir;
    });

    return sorted;
  }, [rows, searchTerm, filterStatus, sortField, sortDirection]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="mb-4 lg:mb-0">
          <h3 className="text-lg font-semibold text-foreground mb-1">Estado de Cumplimiento por Instrumento</h3>
          <p className="text-sm text-muted-foreground">Detalle de certificaciones y próximos vencimientos</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="search"
            placeholder="Buscar instrumentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full sm:w-64"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-muted-foreground">Cargando…</div>
      ) : error ? (
        <div className="p-6 text-sm text-destructive">Error al cargar: {error.message}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4">
                    <button onClick={() => handleSort('instrument')} className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground">
                      <span>Instrumento</span><Icon name="ArrowUpDown" size={14} />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4">
                    <button onClick={() => handleSort('type')} className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground">
                      <span>Tipo</span><Icon name="ArrowUpDown" size={14} />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4"><span className="text-sm font-medium text-muted-foreground">Estado</span></th>
                  <th className="text-left py-3 px-4">
                    <button onClick={() => handleSort('nextDue')} className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground">
                      <span>Próximo Venc.</span><Icon name="ArrowUpDown" size={14} />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4">
                    <button onClick={() => handleSort('accuracy')} className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground">
                      <span>Precisión</span><Icon name="ArrowUpDown" size={14} />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4"><span className="text-sm font-medium text-muted-foreground">Técnico</span></th>
                  <th className="text-center py-3 px-4"><span className="text-sm font-medium text-muted-foreground">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.map(item => (
                  <tr key={item.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-foreground text-sm">{item.instrument}</div>
                        <div className="text-xs text-muted-foreground">{item.tag} • {item.serial}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-foreground">{item.type}</span>
                      <div className="text-xs text-muted-foreground">{item.certification}</div>
                    </td>
                    <td className="py-4 px-4">
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                        <Icon name={getStatusIcon(item.status)} size={12} /><span>{getStatusLabel(item.status)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-foreground">{formatDDMMYYYY(item.nextDue)}</div>
                      <div className="text-xs text-muted-foreground">Últ: {formatDDMMYYYY(item.lastCalibration)}</div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm font-medium text-foreground">{item.accuracy != null ? `${item.accuracy}%` : '—'}</span>
                    </td>
                    <td className="py-4 px-4"><span className="text-sm text-foreground">{item.technician}</span></td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="ghost" size="sm" iconName="Eye" title="Ver detalles" />
                        <Button variant="ghost" size="sm" iconName="FileText" title="Certificado" />
                        <Button variant="ghost" size="sm" iconName="Calendar" title="Programar" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAndSorted.length === 0 && (
            <div className="text-center py-8">
              <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No se encontraron instrumentos que coincidan con los criterios de búsqueda.</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground mb-4 sm:mb-0">
              Mostrando {filteredAndSorted.length} de {rows.length} instrumentos
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" iconName="FileDown" onClick={() => exportCsv(filteredAndSorted)}>Exportar CSV</Button>
              <Button variant="outline" size="sm" iconName="Printer" onClick={() => window.print()}>Imprimir</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ComplianceGrid;
